/**
 * Fleet Gantt — vehicles down the side, time across the top.
 *
 * The default schedule view, because a courtesy car stays with one customer
 * for the whole repair: what you need to see is which cars are tied up and
 * where the gaps are, which a month grid of counts can't tell you.
 *
 * Bars can be dragged to reschedule and their edges pulled to extend a loan.
 * Clashes stack rather than hide, and a bar that would land on top of another
 * turns red before it is dropped.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  addDays,
  differenceInCalendarDays,
  eachDayOfInterval,
  endOfDay,
  format,
  isSameDay,
  isWeekend,
  startOfDay
} from 'date-fns';
import { AlertTriangle, ArrowLeft, ArrowRight, Wrench } from 'lucide-react';

import {
  bookingInterval,
  bookingPosition,
  bookingVehicleId,
  findConflicts,
  isOpenEnded,
  isOverdue,
  packBookingRows,
  vehicleComplianceStatus,
  vehicleLabel,
  vehicleStateAt,
  VEHICLE_STATE_META
} from '../../lib/fleet';
import { BOOKING_TONE_STYLES, bookingTone, COMPLIANCE_TONE_STYLES, VEHICLE_STATE_STYLES } from './fleetStyles';
import { cn } from '../../lib/utils';

const RAIL_WIDTH = 248;
const MIN_DAY_WIDTH = 34;
const SUB_ROW_HEIGHT = 30;
const SUB_ROW_GAP = 4;
const LANE_PADDING = 8;
const DRAG_THRESHOLD_PX = 3;

/**
 * Modes a booking allows: a car already out can only have its return moved,
 * and one with no return date has no end to take hold of.
 */
function allowedModes(booking) {
  if (['returned', 'cancelled', 'no_show'].includes(booking.status)) return [];
  const open = isOpenEnded(booking);
  if (booking.status === 'out') return open ? [] : ['resize-end'];
  return open ? ['move', 'resize-start'] : ['move', 'resize-start', 'resize-end'];
}

function BarLabel({ booking, priv }) {
  const customer = booking.customer;
  const name =
    typeof customer === 'object' && customer
      ? priv.name(`${customer.firstName || ''} ${customer.lastName || ''}`.trim())
      : '';
  const fallback = booking.title || booking.driverName || '';
  return <span className="truncate">{name || priv.name(fallback) || 'Booking'}</span>;
}

const FleetGantt = ({
  vehicles,
  bookings,
  rangeStart,
  rangeEnd,
  now = new Date(),
  priv = { name: (v) => v, rego: (v) => v },
  onSelectBooking,
  onCreateBooking,
  onMoveBooking
}) => {
  const [laneWidth, setLaneWidth] = useState(0);
  const [drag, setDrag] = useState(null);
  const laneProbeRef = useRef(null);
  const laneRectsRef = useRef([]);
  const dragRef = useRef(null);

  const days = useMemo(
    () => eachDayOfInterval({ start: startOfDay(rangeStart), end: startOfDay(rangeEnd) }),
    [rangeStart, rangeEnd]
  );
  const totalDays = days.length;

  // Percentage geometry needs a real pixel width to convert a drag into days.
  useEffect(() => {
    const el = laneProbeRef.current;
    if (!el) return undefined;
    const measure = () => setLaneWidth(el.getBoundingClientRect().width);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [totalDays, vehicles.length]);

  const pxPerDay = laneWidth && totalDays ? laneWidth / totalDays : 0;

  const rangeFrom = useMemo(() => startOfDay(rangeStart), [rangeStart]);
  const rangeTo = useMemo(() => endOfDay(rangeEnd), [rangeEnd]);

  const byVehicle = useMemo(() => {
    const map = new Map();
    vehicles.forEach((v) => map.set(v.$id, []));
    bookings.forEach((b) => {
      const id = bookingVehicleId(b);
      if (id && map.has(id)) map.get(id).push(b);
    });
    return map;
  }, [vehicles, bookings]);

  // ---------------------------------------------------------------
  // Drag & resize
  // ---------------------------------------------------------------

  const registerLane = useCallback((vehicleId, el) => {
    laneRectsRef.current = laneRectsRef.current.filter((entry) => entry.vehicleId !== vehicleId);
    if (el) laneRectsRef.current.push({ vehicleId, el });
  }, []);

  const vehicleAtPoint = useCallback((clientY) => {
    const hit = laneRectsRef.current.find(({ el }) => {
      const rect = el.getBoundingClientRect();
      return clientY >= rect.top && clientY <= rect.bottom;
    });
    return hit ? hit.vehicleId : null;
  }, []);

  const beginDrag = (event, booking, mode) => {
    if (!pxPerDay) return;
    if (!allowedModes(booking).includes(mode)) return;
    event.stopPropagation();
    event.preventDefault();

    const { start, end, openEnded } = bookingInterval(booking, now);
    const state = {
      kind: 'booking',
      booking,
      mode,
      openEnded,
      originX: event.clientX,
      baseStart: start,
      baseEnd: end,
      vehicleId: bookingVehicleId(booking),
      dayDelta: 0,
      moved: false
    };
    dragRef.current = state;
    setDrag({ ...state });
  };

  const beginCreate = (event, vehicleId) => {
    if (!pxPerDay || !onCreateBooking) return;
    // Ignore anything that started on a bar.
    if (event.target.closest?.('[data-booking-bar]')) return;

    const rect = event.currentTarget.getBoundingClientRect();
    const dayIndex = Math.floor((event.clientX - rect.left) / pxPerDay);
    const anchor = days[Math.max(0, Math.min(totalDays - 1, dayIndex))];

    const state = {
      kind: 'create',
      vehicleId,
      originX: event.clientX,
      anchor,
      dayDelta: 0,
      moved: false
    };
    dragRef.current = state;
    setDrag({ ...state });
  };

  useEffect(() => {
    if (!drag) return undefined;

    const handleMove = (event) => {
      const state = dragRef.current;
      if (!state) return;
      const dx = event.clientX - state.originX;
      if (Math.abs(dx) > DRAG_THRESHOLD_PX) state.moved = true;
      state.dayDelta = Math.round(dx / pxPerDay);

      if (state.kind === 'booking' && state.mode === 'move') {
        const overVehicle = vehicleAtPoint(event.clientY);
        if (overVehicle) state.vehicleId = overVehicle;
      }

      setDrag({ ...state });
    };

    const handleUp = async () => {
      const state = dragRef.current;
      dragRef.current = null;
      setDrag(null);
      if (!state) return;

      if (state.kind === 'create') {
        if (!onCreateBooking) return;
        const a = state.anchor;
        const b = addDays(state.anchor, state.dayDelta);
        const start = a <= b ? a : b;
        const end = a <= b ? b : a;
        onCreateBooking({
          vehicleId: state.vehicleId,
          scheduledStart: startOfDay(start),
          // A click with no drag opens a one-day booking; a drag uses the swept range.
          scheduledEnd: endOfDay(state.moved ? end : start)
        });
        return;
      }

      const preview = previewFor(state);
      if (!preview) return;

      const unchanged =
        preview.start.getTime() === state.baseStart.getTime() &&
        preview.end.getTime() === state.baseEnd.getTime() &&
        preview.vehicleId === bookingVehicleId(state.booking);

      if (!state.moved || unchanged) {
        if (!state.moved) onSelectBooking?.(state.booking);
        return;
      }

      onMoveBooking?.(state.booking, {
        scheduledStart: preview.start.toISOString(),
        // Leave an open-ended booking open; only its start can move.
        ...(preview.openEnded ? {} : { scheduledEnd: preview.end.toISOString() }),
        vehicle: preview.vehicleId || null
      });
    };

    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
    window.addEventListener('pointercancel', handleUp);
    return () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
      window.removeEventListener('pointercancel', handleUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drag?.kind, pxPerDay, vehicleAtPoint, onCreateBooking, onMoveBooking, onSelectBooking]);

  /** Where a dragged booking currently sits, clamped to at least one day long. */
  const previewFor = useCallback((state) => {
    if (!state || state.kind !== 'booking') return null;
    const { mode, dayDelta, baseStart, baseEnd, openEnded } = state;

    let start = baseStart;
    let end = baseEnd;

    // An open-ended booking has no real end to shift — moving it or pulling its
    // start only changes when it began.
    if (openEnded) {
      if (mode === 'move' || mode === 'resize-start') start = addDays(baseStart, dayDelta);
      return { start, end, vehicleId: state.vehicleId, openEnded: true };
    }

    if (mode === 'move') {
      start = addDays(baseStart, dayDelta);
      end = addDays(baseEnd, dayDelta);
    } else if (mode === 'resize-start') {
      start = addDays(baseStart, dayDelta);
      if (start >= end) start = addDays(end, -1);
    } else if (mode === 'resize-end') {
      end = addDays(baseEnd, dayDelta);
      if (end <= start) end = addDays(start, 1);
    }

    return { start, end, vehicleId: state.vehicleId };
  }, []);

  const activePreview = drag?.kind === 'booking' ? previewFor(drag) : null;

  /** Would the dragged booking land on top of another? */
  const previewConflicts = useMemo(() => {
    if (!activePreview || !drag?.booking) return [];
    return findConflicts(
      {
        ...drag.booking,
        vehicle: activePreview.vehicleId,
        scheduledStart: activePreview.start.toISOString(),
        scheduledEnd: activePreview.end.toISOString(),
        // The plan is what is being moved; ignore any stamped handover time so
        // the preview reflects where the bar actually is.
        actualStart: null,
        actualEnd: null
      },
      bookings,
      now
    );
  }, [activePreview, drag, bookings, now]);

  const hasPreviewConflict = previewConflicts.length > 0;

  // ---------------------------------------------------------------
  // Rendering
  // ---------------------------------------------------------------

  const todayOffset = useMemo(() => {
    const index = days.findIndex((d) => isSameDay(d, now));
    if (index === -1) return null;
    const fraction = (now.getHours() * 60 + now.getMinutes()) / 1440;
    return ((index + fraction) / totalDays) * 100;
  }, [days, totalDays, now]);

  const gridWidth = Math.max(totalDays * MIN_DAY_WIDTH, 480);
  const dense = totalDays > 21;

  const renderBar = (booking, subRow) => {
    const isDragging = drag?.kind === 'booking' && drag.booking.$id === booking.$id;
    const preview = isDragging ? activePreview : null;

    const effective = preview
      ? {
          ...booking,
          scheduledStart: preview.start.toISOString(),
          scheduledEnd: preview.openEnded ? null : preview.end.toISOString(),
          actualStart: null,
          actualEnd: null
        }
      : booking;

    const position = bookingPosition(effective, rangeFrom, rangeTo, now);
    if (!position) return null;

    const tone = BOOKING_TONE_STYLES[bookingTone(booking, now)];
    const modes = allowedModes(booking);
    const canMove = modes.includes('move');
    const { start, end, openEnded } = bookingInterval(effective, now);
    const nights = openEnded ? null : Math.max(1, differenceInCalendarDays(end, start));

    return (
      <div
        key={booking.$id}
        data-booking-bar
        role="button"
        tabIndex={0}
        aria-label={`${tone.label} booking, from ${format(start, 'd MMM')} ${openEnded ? 'ongoing' : `to ${format(end, 'd MMM')}`}`}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelectBooking?.(booking);
          }
        }}
        onPointerDown={(e) => (canMove ? beginDrag(e, booking, 'move') : undefined)}
        onClick={(e) => {
          e.stopPropagation();
          if (!canMove) onSelectBooking?.(booking);
        }}
        title={`${tone.label} · ${format(start, 'd MMM')} → ${openEnded ? 'ongoing' : format(end, 'd MMM')}`}
        className={cn(
          'group absolute flex items-center gap-1 rounded-md border px-2 text-xs font-medium shadow-sm transition-shadow',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          tone.bar,
          canMove ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer',
          isDragging && 'z-30 shadow-lg ring-2 ring-primary/60',
          isDragging && hasPreviewConflict && 'ring-2 ring-red-500 border-red-500'
        )}
        style={{
          left: `${position.left}%`,
          width: `${position.width}%`,
          top: LANE_PADDING + subRow * (SUB_ROW_HEIGHT + SUB_ROW_GAP),
          height: SUB_ROW_HEIGHT
        }}
      >
        {position.clippedStart && <ArrowLeft className="h-3 w-3 shrink-0 opacity-60" />}

        {modes.includes('resize-start') && (
          <span
            role="presentation"
            onPointerDown={(e) => beginDrag(e, booking, 'resize-start')}
            className="absolute left-0 top-0 h-full w-2 cursor-ew-resize rounded-l-md opacity-0 group-hover:opacity-100 group-hover:bg-foreground/10"
          />
        )}

        {booking.type === 'maintenance' && <Wrench className="h-3 w-3 shrink-0" />}
        {isOverdue(booking, now) && <AlertTriangle className="h-3 w-3 shrink-0" />}

        <BarLabel booking={booking} priv={priv} />

        {position.width > 12 && (
          <span className="ml-auto shrink-0 tabular-nums opacity-70">{openEnded ? '∞' : `${nights}d`}</span>
        )}

        {modes.includes('resize-end') && (
          <span
            role="presentation"
            onPointerDown={(e) => beginDrag(e, booking, 'resize-end')}
            className="absolute right-0 top-0 h-full w-2 cursor-ew-resize rounded-r-md opacity-0 group-hover:opacity-100 group-hover:bg-foreground/10"
          />
        )}

        {position.clippedEnd && <ArrowRight className="h-3 w-3 shrink-0 opacity-60" />}
      </div>
    );
  };

  const renderLaneBackground = () => (
    <>
      {days.map((day, index) => (
        <div
          key={day.toISOString()}
          className={cn(
            'absolute top-0 h-full border-l border-border/60',
            isWeekend(day) && 'bg-muted/40',
            isSameDay(day, now) && 'bg-primary/5'
          )}
          style={{ left: `${(index / totalDays) * 100}%`, width: `${(1 / totalDays) * 100}%` }}
        />
      ))}
      {todayOffset !== null && (
        <div
          className="pointer-events-none absolute top-0 z-10 h-full w-px bg-primary"
          style={{ left: `${todayOffset}%` }}
        />
      )}
    </>
  );

  return (
    <div className="overflow-x-auto">
      <div style={{ minWidth: RAIL_WIDTH + gridWidth }}>
        {/* Day headers */}
        <div className="sticky top-0 z-30 flex border-b bg-card">
          <div
            className="sticky left-0 z-10 shrink-0 border-r bg-card px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
            style={{ width: RAIL_WIDTH }}
          >
            Vehicle
          </div>
          <div className="relative flex flex-1">
            {days.map((day) => (
              <div
                key={day.toISOString()}
                className={cn(
                  'flex flex-1 flex-col items-center border-l border-border/60 py-1.5 text-center',
                  isWeekend(day) && 'bg-muted/40',
                  isSameDay(day, now) && 'bg-primary/10'
                )}
              >
                {!dense && (
                  <span className="text-[10px] uppercase text-muted-foreground">{format(day, 'EEE')}</span>
                )}
                <span
                  className={cn(
                    'text-xs tabular-nums',
                    isSameDay(day, now) ? 'font-bold text-primary' : 'text-foreground'
                  )}
                >
                  {format(day, dense ? 'd' : 'd MMM')}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Vehicle lanes */}
        {vehicles.length === 0 ? (
          <div className="p-10 text-center text-sm text-muted-foreground">
            No vehicles match the current filters.
          </div>
        ) : (
          vehicles.map((vehicle, vehicleIndex) => {
            let laneBookings = byVehicle.get(vehicle.$id) || [];

            // A booking dragged onto another vehicle is drawn in the lane it is
            // headed for, not the one it came from, so the bar follows the cursor.
            if (drag?.kind === 'booking' && drag.mode === 'move') {
              const origin = bookingVehicleId(drag.booking);
              if (origin !== drag.vehicleId) {
                if (vehicle.$id === origin) {
                  laneBookings = laneBookings.filter((b) => b.$id !== drag.booking.$id);
                } else if (vehicle.$id === drag.vehicleId) {
                  laneBookings = [...laneBookings, drag.booking];
                }
              }
            }

            const rows = packBookingRows(laneBookings, now);
            const laneHeight = Math.max(
              rows.length * (SUB_ROW_HEIGHT + SUB_ROW_GAP) + LANE_PADDING * 2 - SUB_ROW_GAP,
              SUB_ROW_HEIGHT + LANE_PADDING * 2
            );
            const state = vehicleStateAt(vehicle, bookings, now);
            const compliance = vehicleComplianceStatus(vehicle, now);
            const isDropTarget = drag?.kind === 'booking' && drag.mode === 'move' && drag.vehicleId === vehicle.$id;

            return (
              <div key={vehicle.$id} className="flex border-b last:border-b-0">
                <div
                  className={cn(
                    'sticky left-0 z-20 shrink-0 border-r bg-card px-3 py-2',
                    vehicleIndex % 2 === 1 && 'bg-muted/20'
                  )}
                  style={{ width: RAIL_WIDTH }}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold">{vehicleLabel(vehicle)}</div>
                      <div className="truncate font-mono text-xs text-muted-foreground">
                        {priv.rego(vehicle.registration)}
                      </div>
                    </div>
                    <span
                      className={cn(
                        'shrink-0 rounded-full border px-1.5 py-0.5 text-[10px] font-medium',
                        VEHICLE_STATE_STYLES[state]
                      )}
                    >
                      {VEHICLE_STATE_META[state]?.label}
                    </span>
                  </div>
                  {(compliance === 'expired' || compliance === 'due_soon') && (
                    <span
                      className={cn(
                        'mt-1 inline-block rounded border px-1.5 py-0.5 text-[10px] font-medium',
                        COMPLIANCE_TONE_STYLES[compliance]
                      )}
                    >
                      {compliance === 'expired' ? 'Compliance expired' : 'Compliance due'}
                    </span>
                  )}
                </div>

                <div
                  ref={(el) => {
                    registerLane(vehicle.$id, el);
                    if (vehicleIndex === 0) laneProbeRef.current = el;
                  }}
                  onPointerDown={(e) => beginCreate(e, vehicle.$id)}
                  className={cn(
                    'relative flex-1 cursor-copy',
                    vehicleIndex % 2 === 1 && 'bg-muted/20',
                    isDropTarget && 'bg-primary/5'
                  )}
                  style={{ height: laneHeight }}
                >
                  {renderLaneBackground()}

                  {/* Range being swept out to create a booking */}
                  {drag?.kind === 'create' && drag.vehicleId === vehicle.$id && drag.moved && (() => {
                    const a = drag.anchor;
                    const b = addDays(drag.anchor, drag.dayDelta);
                    const position = bookingPosition(
                      {
                        scheduledStart: (a <= b ? a : b).toISOString(),
                        scheduledEnd: endOfDay(a <= b ? b : a).toISOString()
                      },
                      rangeFrom,
                      rangeTo,
                      now
                    );
                    if (!position) return null;
                    return (
                      <div
                        className="pointer-events-none absolute rounded-md border-2 border-dashed border-primary bg-primary/10"
                        style={{
                          left: `${position.left}%`,
                          width: `${position.width}%`,
                          top: LANE_PADDING,
                          height: SUB_ROW_HEIGHT
                        }}
                      />
                    );
                  })()}

                  {rows.map((row, rowIndex) => row.map((booking) => renderBar(booking, rowIndex)))}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default FleetGantt;
